import {
  Form,
  Link,
  useLocation,
  useNavigate,
  useSubmit,
  useFetcher,
} from "@remix-run/react";
import { Search, Menu, Settings2 } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LandingHeader } from "#app/components/landing/landing-header";
import { SettingsModal } from "#app/components/settings-modal";
import { Avatar, AvatarFallback, AvatarImage } from "#app/components/ui/avatar";
import { Button } from "#app/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "#app/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "#app/components/ui/sheet";
import { type loader as settingsDataLoader } from "#app/routes/resources+/settings-data";
import { clearClientAuthStateOnLogout } from "#app/utils/auth.client";
import { ProviderConnectionForm } from "#app/utils/connections";
import { getLocalizedDisplayName } from "#app/utils/display-name";
import { cn, getUserImgSrc } from "#app/utils/misc";
import { useOptionalUser } from "#app/utils/user";

type Tab = "docs" | "tutorials";

export function DocTutorialShell({
  activeTab,
  leftNav,
  rightRail,
  children,
}: {
  activeTab: Tab;
  leftNav: ReactNode;
  rightRail?: ReactNode;
  children: ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const user = useOptionalUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const logoutFormRef = useRef<HTMLFormElement>(null);
  const searchDebounceRef = useRef<number | null>(null);
  const settingsFetcher = useFetcher<typeof settingsDataLoader>();
  const isSettingsLoadingRef = useRef(false);
  const { t, i18n } = useTranslation(["docs", "common"]);
  const displayName = user
    ? getLocalizedDisplayName(user, i18n.language) || user.id
    : "";
  const userInitial = user?.username?.charAt(0)?.toUpperCase() ?? "?";

  const urlQuery = useMemo(() => {
    const q = new URLSearchParams(location.search).get("q") ?? "";
    return q;
  }, [location.search]);

  const tabHref = useMemo(() => {
    const isInDocs = location.pathname.startsWith("/docs");
    const isInTutorials = location.pathname.startsWith("/u/alpha/tutorials");
    return {
      docs: isInDocs ? location.pathname : "/docs",
      tutorials: isInTutorials ? location.pathname : "/u/alpha/tutorials",
    };
  }, [location.pathname]);

  useEffect(() => {
    document.querySelector(".sidebar")?.classList.remove("open");
    document.querySelector(".layout-right-block")?.classList.remove("move");
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    // Keep input in sync with URL query for the active section.
    if (activeTab === "docs" && location.pathname.startsWith("/docs")) {
      setSearchQuery(urlQuery);
      return;
    }
    if (
      activeTab === "tutorials" &&
      location.pathname.startsWith("/u/alpha/tutorials")
    ) {
      setSearchQuery(urlQuery);
    }
  }, [activeTab, location.pathname, urlQuery]);

  useEffect(() => {
    // Live search: update URL as user types (debounced) so the landing page filters instantly.
    if (activeTab !== "docs" && activeTab !== "tutorials") return;

    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = window.setTimeout(() => {
      const q = searchQuery.trim();
      const base = activeTab === "docs" ? "/docs" : "/u/alpha/tutorials";
      const target =
        q.length === 0 ? base : `${base}?q=${encodeURIComponent(q)}`;

      const isIndex =
        location.pathname === base || location.pathname === `${base}/`;

      // Don't auto-redirect away from a page just because `q` is empty.
      // If the user types a query while reading a page, we *do* take them to the index with results.
      if (!isIndex && q.length === 0) return;

      const current = `${location.pathname}${location.search}`;
      if (current === target) return;

      // If you're already on the index, this will just update query params.
      // If you're reading a page, it will take you to the index with results.
      navigate(target, { replace: true });
    }, 200);

    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [activeTab, location.pathname, location.search, navigate, searchQuery]);

  // Load settings data when modal opens
  useEffect(() => {
    if (
      settingsOpen &&
      !settingsFetcher.data &&
      !isSettingsLoadingRef.current &&
      settingsFetcher.state === "idle"
    ) {
      isSettingsLoadingRef.current = true;
      settingsFetcher.load("/resources/settings-data");
    }
  }, [
    settingsOpen,
    settingsFetcher,
    settingsFetcher.data,
    settingsFetcher.state,
  ]);

  useEffect(() => {
    if (settingsFetcher.state === "idle") {
      isSettingsLoadingRef.current = false;
    }
  }, [settingsFetcher.state]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (activeTab !== "docs" && activeTab !== "tutorials") return;
    const base = activeTab === "docs" ? "/docs" : "/u/alpha/tutorials";
    if (!q) navigate(base);
    else navigate(`${base}?q=${encodeURIComponent(q)}`);
  };

  const redirectTo = useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search],
  );

  const backTo = user ? "/u/alpha/agent" : "/marketplace";

  return (
    <>
      {/* Fixed overlay: clips all content so only the three columns scroll; no full-page scroll */}
      <div
        className="fixed inset-0 flex flex-col overflow-hidden bg-white dark:bg-zinc-900"
        style={{ minHeight: 0 }}
      >
        <header className="shrink-0 border-b border-gray-200 bg-white z-10 relative dark:border-zinc-700 dark:bg-zinc-900">
          <LandingHeader showLogin={!user} backTo={backTo}>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      variant="secondary"
                      className="flex items-center gap-2 h-auto p-1.5! pr-3!"
                    />
                  }
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      src={getUserImgSrc(user.image?.id) ?? undefined}
                      alt={displayName}
                    />
                    <AvatarFallback className="bg-sky-600 text-sm font-medium text-white">
                      {userInitial}
                    </AvatarFallback>
                  </Avatar>
                  <span className="max-w-48 truncate text-sm font-medium">
                    {displayName}
                  </span>
                </DropdownMenuTrigger>
                <DropdownMenuContent sideOffset={8} align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      setSettingsOpen(true);
                      if (settingsFetcher.state === "idle") {
                        isSettingsLoadingRef.current = true;
                        settingsFetcher.load("/resources/settings-data");
                      }
                    }}
                  >
                    <Settings2 className="mr-2 h-4 w-4" />
                    {t("common:settings")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(event) => {
                      event.preventDefault();
                      clearClientAuthStateOnLogout();
                      submit(logoutFormRef.current);
                    }}
                    render={
                      <Form
                        action="/logout"
                        method="POST"
                        ref={logoutFormRef}
                        onSubmit={() => {
                          clearClientAuthStateOnLogout();
                        }}
                      />
                    }
                  >
                    <button type="submit">{t("common:logout")}</button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <ProviderConnectionForm
                type="Login"
                providerName="oauth2"
                redirectTo={redirectTo}
                buttonText={t("common:tryForFree")}
                buttonClassName="flex h-10 items-center justify-center rounded-full px-6 py-2.5 text-sm  capitalize text-white transition-opacity hover:opacity-95 focus-visible:ring-2 focus-visible:ring-white/30 bg-[linear-gradient(93.53deg,#141A42_14.58%,#6577A4_110.19%)]"
              />
            )}
          </LandingHeader>

          {/* Search and Tabs subheader */}
          <div className="pb-4 pt-4 border-t border-zinc-100 dark:border-zinc-700">
            <div className=" container flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
              <div className="flex items-center gap-3">
                {/* Mobile nav drawer */}
                <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                  <SheetTrigger
                    render={
                      <button
                        type="button"
                        className="lg:hidden p-2 hover:bg-gray-100 rounded-md dark:hover:bg-zinc-800"
                        aria-label={t("docs:openNavigation")}
                      />
                    }
                  >
                    <Menu className="h-5 w-5" />
                  </SheetTrigger>
                  <SheetContent
                    side="left"
                    className="w-72 overflow-y-auto p-4"
                  >
                    {leftNav}
                  </SheetContent>
                </Sheet>
                <div className="flex items-center gap-8 pl-2 lg:pl-0">
                  <Link
                    to={tabHref.docs}
                    className={cn(
                      "relative py-2 text-sm font-medium transition-colors",
                      activeTab === "docs"
                        ? "text-[#535EA4]"
                        : "text-slate-700 hover:text-slate-900 dark:text-zinc-300 dark:hover:text-zinc-100",
                    )}
                  >
                    {t("docs:documentation")}
                    {activeTab === "docs" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#535EA4]" />
                    )}
                  </Link>
                  <Link
                    to={tabHref.tutorials}
                    className={cn(
                      "relative py-2 text-sm font-medium transition-colors",
                      activeTab === "tutorials"
                        ? "text-[#535EA4]"
                        : "text-slate-700 hover:text-slate-900 dark:text-zinc-300 dark:hover:text-zinc-100",
                    )}
                  >
                    {t("docs:tutorials")}
                    {activeTab === "tutorials" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#535EA4]" />
                    )}
                  </Link>
                </div>
              </div>

              {/* Search (right-aligned) */}
              <div className="ml-auto w-full  sm:w-auto sm:flex-1 sm:max-w-md">
                <form onSubmit={handleSearch} className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={
                      activeTab === "docs"
                        ? t("docs:searchDocs")
                        : activeTab === "tutorials"
                          ? t("docs:searchTutorials")
                          : t("common:search")
                    }
                    className="w-full h-10 pl-10 pr-4 rounded-full bg-zinc-100 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#535EA4]/20 focus:border-[#535EA4] transition-colors dark:border-zinc-700 dark:bg-zinc-800"
                  />
                </form>
              </div>
            </div>
          </div>
        </header>

        {/* Three columns: each has its own scroll; no full-page scroll */}
        <div
          className="flex flex-1 flex-row overflow-hidden"
          style={{ minHeight: 0 }}
        >
          {/* Left column - desktop only; mobile uses Sheet overlay */}
          <aside
            className="hidden lg:block w-64 shrink-0 overflow-y-auto border-r border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900"
            style={{ minHeight: 0 }}
          >
            <div className="p-4">{leftNav}</div>
          </aside>

          {/* Center column - doc content, scrolls in viewport only */}
          <main
            className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto bg-white dark:bg-zinc-900"
            style={{ minHeight: 0 }}
            data-docs-scroll-root
          >
            <div className="max-w-4xl mx-auto px-4 py-8 sm:px-8">
              {children}
            </div>
          </main>

          {/* Right column - On this page, scrolls in viewport only */}
          {rightRail && (
            <aside
              className="w-64 shrink-0 overflow-y-auto border-l border-gray-200 bg-white hidden xl:block dark:border-zinc-700 dark:bg-zinc-900"
              style={{ minHeight: 0 }}
            >
              <div className="p-4">{rightRail}</div>
            </aside>
          )}
        </div>
      </div>

      {/* Outside the overflow-hidden shell so the dialog portal stacks correctly */}
      {user ? (
        <SettingsModal
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          user={
            settingsFetcher.data && "user" in settingsFetcher.data
              ? settingsFetcher.data.user
              : {
                  id: user.id,
                  name: user.name,
                  username: user.username,
                  email: user.email || user.username,
                  image: user.image,
                  _count: { sessions: 0 },
                }
          }
          hasPassword={
            settingsFetcher.data && "hasPassword" in settingsFetcher.data
              ? settingsFetcher.data.hasPassword
              : false
          }
          isTwoFactorEnabled={
            settingsFetcher.data && "isTwoFactorEnabled" in settingsFetcher.data
              ? settingsFetcher.data.isTwoFactorEnabled
              : false
          }
          keycloakId={
            settingsFetcher.data && "keycloakId" in settingsFetcher.data
              ? settingsFetcher.data.keycloakId
              : null
          }
          onUpdate={() => {
            if (settingsFetcher.state === "idle") {
              isSettingsLoadingRef.current = false;
              settingsFetcher.load("/resources/settings-data");
            }
          }}
        />
      ) : null}
    </>
  );
}
